/**
 * Wishlist Service - API calls for coupon wishlist functionality
 */

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Save a coupon to the customer's wishlist
 * @param {string} customerToken - Customer's authentication token
 * @param {number} couponId - ID of the coupon to save
 * @param {number} venueId - ID of the venue
 * @returns {Promise<Object>} - Saved coupon data
 */
export const saveCouponToWishlist = async (customerToken, couponId, venueId) => {
  try {
    const response = await fetch(`${apiUrl}/loyalty/wishlist/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        customer_token: customerToken,
        coupon_id: couponId,
        venue_id: venueId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to save coupon to wishlist");
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving coupon to wishlist:", error);
    throw error;
  }
};

/**
 * Remove a saved coupon from the wishlist
 * @param {number} savedId - ID of the saved coupon entry
 * @param {string} customerToken - Customer's authentication token
 * @returns {Promise<Object>} - Removal confirmation
 */
export const removeSavedCoupon = async (savedId, customerToken) => {
  try {
    const response = await fetch(
      `${apiUrl}/loyalty/wishlist/${savedId}?customer_token=${customerToken}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to remove coupon from wishlist");
    }

    return await response.json();
  } catch (error) {
    console.error("Error removing coupon from wishlist:", error);
    throw error;
  }
};

/**
 * Get all saved coupons for a customer
 * @param {string} customerToken - Customer's authentication token
 * @returns {Promise<Object>} - Wishlist data with saved coupons
 */
export const getCustomerWishlist = async (customerToken) => {
  try {
    const response = await fetch(
      `${apiUrl}/loyalty/wishlist/${customerToken}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to fetch wishlist");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching wishlist:", error);
    throw error;
  }
};

/**
 * Check if a coupon is in the wishlist
 * @param {Array} wishlistCoupons - Array of saved coupons
 * @param {number} couponId - Coupon ID to check
 * @returns {Object|null} - Saved coupon object if found, null otherwise
 */
export const isCouponInWishlist = (wishlistCoupons, couponId) => {
  if (!wishlistCoupons || !Array.isArray(wishlistCoupons)) {
    return null;
  }
  return wishlistCoupons.find((item) => item.coupon_id === couponId) || null;
};
